import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function LicenseValidationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [email, setEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const sRes = await api.get("/settings");
      const code = sRes.data?.agency_pairing_code;
      const hub = sRes.data?.agency_hub_url;
      if (!code || !hub) { setStatus({ unpaired: true }); return; }
      const r = await fetch(`${hub.replace(/\/+$/, "")}/api/agency/public-license/${code}/validation-status`);
      if (!r.ok) {
        if (r.status === 404) { setStatus({ unsupported: true, pairing_code: code, hub_url: hub }); return; }
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      setStatus({ ...data, pairing_code: code, hub_url: hub });
    } catch (e) {
      setStatus({ error: e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!accepted) { toast.error("Cochez la case d'acceptation"); return; }
    if (!email.trim()) { toast.error("Indiquez votre email"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(
        `${status.hub_url.replace(/\/+$/, "")}/api/agency/public-license/${status.pairing_code}/client-validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accepted: true, validator_email: email.trim() }),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      toast.success("Site validé — votre abonnement démarre maintenant ✓");
      load();
    } catch (e) {
      toast.error("Validation échouée: " + e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6 flex items-center justify-center" data-testid="license-card-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-sky)]" />
    </div>
  );

  if (status?.unpaired || status?.unsupported || status?.error) return null; // silent fallback if HUB doesn't expose this yet

  if (status?.validated) return (
    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6" data-testid="license-card-validated">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center text-white shrink-0">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-emerald-900 text-lg">Site validé et en ligne ✓</h3>
          <p className="text-sm text-emerald-800 mt-1">
            Validé le {status.validated_at ? new Date(status.validated_at).toLocaleDateString("fr-FR") : "—"}
            {status.validated_by_email ? <> par <strong>{status.validated_by_email}</strong></> : ""}.
          </p>
          {status.license_started_at && (
            <p className="text-xs text-emerald-700 mt-1">
              Abonnement actif depuis le {new Date(status.license_started_at).toLocaleDateString("fr-FR")}.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border-2 border-sky-200 rounded-2xl p-6" data-testid="license-card-pending">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center text-white shrink-0">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">
            Validation de livraison de votre site
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Votre site « <strong>{status?.site_label || status?.company_name || "Kloub'Air Plomberie"}</strong> » est prêt.
            Validez la livraison pour démarrer votre abonnement.
          </p>
        </div>
      </div>

      {status?.legal_text && (
        <div className="bg-white border border-sky-100 rounded-lg p-4 mb-4 text-sm leading-relaxed text-slate-700 whitespace-pre-line max-h-40 overflow-y-auto">
          {status.legal_text}
        </div>
      )}

      <label className="flex items-start gap-2 mb-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1 w-4 h-4 accent-emerald-600"
          data-testid="license-accept-checkbox"
        />
        <span className="text-sm font-semibold text-slate-800">
          J'accepte les conditions et je valide la livraison de mon site.
        </span>
      </label>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="vous@exemple.fr"
        className="w-full px-3 py-2 rounded-lg border border-sky-200 bg-white mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
        data-testid="license-validator-email"
      />

      <button
        onClick={submit}
        disabled={!accepted || submitting || !email.trim()}
        className="w-full px-5 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid="license-validate-btn"
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Validation…</span>
        ) : "Je valide la livraison"}
      </button>
    </div>
  );
}
